<template>
  <div :key="key" class="contact-page">
    <div class="contact-page-loader" v-if="loading">
      <loading></loading>
    </div>

    <div class="row gutter-md" v-else>
      <!-- Left column -->
      <div
        class="col-xs-12 col-sm-6 col-md-6 col-lg-offset-4 col-lg-4 col-xl-4"
      >
        <!-- Main contact header -->
        <div class="contact-info">
          <user-image
            :firstname="contact.firstname"
            :lastname="contact.lastname"
            :email="contact.email"
            size="medium"
          />

          <h4>{{ contact.name }}</h4>

          <p v-if="!!contact.email" class="email">{{ contact.email }}</p>
          <p v-else class="email">-</p>

          <p class="caption">
            {{ $t("ui.contact.created") }}
            {{ formatDateTimeNatural(contact.created_at) }}
          </p>

          <p class="caption">
            {{ $t("ui.contact.updated") }}
            {{ formatDateTimeNatural(contact.updated_at) }}
          </p>

          <div class="source">
            <div class="source-icon">
              <img
                src="../../../assets/icon-manual-import.svg"
                v-if="contact.source == 'upload'"
              />
              <img
                src="../../../assets/icon-web-app.svg"
                v-else-if="contact.source == 'web_app'"
              />
              <img
                src="../../../assets/icon-mobile-app.svg"
                v-else-if="contact.source == 'mobile_app'"
              />
              <img
                src="../../../assets/icon-crm.svg"
                v-else-if="contact.source == 'crm_sync'"
              />
              <img
                src="../../../assets/icon-external-form.svg"
                v-else-if="contact.source == 'external_form'"
              />
              <img src="../../../assets/icon-unknown.svg" v-else />
            </div>
            <div class="desc">
              <p class="caption">{{ $t("ui.contact.source") }}</p>

              <p class="body-p">
                {{ $t("ui.contact.sources." + contact.source) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Contact details section -->
        <div class="contact-details">
          <div class="contact-details-header justify-between flex">
            <h5 class="block">{{ $t("ui.contact.contact_details") }}</h5>
            <a
              class="link block blue-text padding-left"
              @click="setIsOpen(true)"
            >
              {{ $t("ui.contact.edit") }}
            </a>
          </div>
          <div v-for="entry in standardPersonalFields" class="info-block">
            <p class="caption">
              {{ $t("ui.contact.caption" + "." + entry.internal_name) }}
            </p>
            <p v-if="entry.val == ''" class="body-p">-</p>
            <p v-else-if="entry.internal_name == 'language'" class="body-p">
              {{ language }}
            </p>
            <p v-else class="body-p">
              {{ entry.val }}
            </p>
          </div>
        </div>
      </div>

      <!-- Middle column -->
      <div class="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-4">
        <!-- Assigned projects list -->
        <div class="lead-panel" v-if="contact.projects.length > 0">
          <p class="caption">{{ $t("ui.contact.is_lead_on") }}</p>
          <div
            :key="'lead-panel-' + project.id"
            v-for="(project, index) in contact.projects"
            class="project-wrapper"
          >
            <q-collapsible :opened="index == 0" class="project-container">
              <!-- custom collapsible header -->
              <template slot="header">
                <div class="project-title">
                  <div class="project-title-label">{{ project.name }}</div>
                  <div class="project-title-sublabel">
                    {{ getProjectSubLabel(project) }}
                  </div>
                  <div class="project-title-classification">
                    <span
                      class="value-classification"
                      :class="project.classification || 'unclassified'"
                      >&nbsp;</span
                    >
                    <a
                      href="#"
                      @click="goToLead(project.id, project.lead_id)"
                      class="link padding-left"
                      >{{ $t("ui.contact.go_to_lead") }}</a
                    >
                  </div>
                </div>
              </template>

              <!-- collapsible content -->
              <div class="leadsheet-details">
                <div
                  v-if="!!leadsheetTabs[project.id]"
                  class="leadsheet-content"
                >
                  <div
                    v-for="(tab, tabName) in leadsheetTabs[project.id]"
                    :key="tabName + project.id"
                    class="leadsheet-tab-wrapper"
                  >
                    <div v-if="tab.length > 0" class="leadsheet-tab">
                      <h5>{{ tabName }}</h5>
                      <span
                        v-for="element in tab"
                        :key="element.name + project.id"
                      >
                        <h6 v-if="element.type == 'title'" class="title-block">
                          {{ element.content }}
                        </h6>
                        <div v-else class="info-block">
                          <p class="caption">{{ element.label }}</p>
                          <p
                            v-if="
                              element.type == 'quantity_selection' &&
                              element.value.length > 0
                            "
                            class="body-p"
                          >
                            <span
                              class="info-aggregate"
                              v-for="quantityVal in element.value"
                              :key="quantityVal.value"
                            >
                              <span class="info-aggregate-label">{{
                                quantityVal.label
                              }}</span>
                              <span class="info-aggregate-value">{{
                                quantityVal.value
                              }}</span>
                            </span>
                          </p>
                          <p
                            v-else-if="
                              Array.isArray(element.value) &&
                              element.value.length > 0
                            "
                            class="body-p"
                          >
                            {{ convertArrayForDisplay(element.value) }}
                          </p>
                          <p
                            v-else-if="
                              Array.isArray(element.value) == false &&
                              !!element.value
                            "
                            class="body-p"
                          >
                            {{ element.value }}
                          </p>
                          <p v-else class="body-p">-</p>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="info-block" v-if="!!tags[project.id]">
                  <p class="caption">{{ $t("ui.contact.tags") }}</p>
                  <p
                    class="body-p"
                    v-if="
                      Array.isArray(tags[project.id]) &&
                      tags[project.id].length > 0
                    "
                  >
                    <span
                      class="tag-list--item"
                      v-for="tag in tags[project.id]"
                      :key="tag.id"
                      >{{ tag.name }}</span
                    >
                  </p>
                  <p v-else class="body-p">-</p>
                </div>
              </div>
            </q-collapsible>
          </div>
        </div>

        <!-- Empty state if contact is a lead in no projects -->
        <div v-else>
          <empty-state
            name="contacts"
            :subtitle="$t('ui.contact.empty_state_text')"
            :button="true"
          ></empty-state>
          <h5></h5>
        </div>
      </div>

      <!-- Right column -->
      <div
        v-if="company && !planIsTalent"
        class="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-4"
      >
        <!-- Company details -->
        <div :key="company.id" class="contact-details">
          <div class="contact-details-header justify-between flex">
            <h5 class="block">{{ $t("ui.contact.company_details") }}</h5>
            <a
              class="link block blue-text padding-left"
              @click="showCompanyChange(true)"
            >
              {{ $t("ui.contact.change_company") }}
            </a>
          </div>
          <div v-for="entry in standardCompanyFields" class="info-block">
            <p v-if="entry.display_name" class="caption">
              {{ $t("ui.company.caption" + "." + entry.internal_name) }}
            </p>
            <p v-if="entry.val == ''" class="body-p">-</p>
            <a
              v-else-if="entry.internal_name == 'name'"
              class="link body-p"
              @click="goToCompany(company.id)"
            >
              {{ entry.val }}
            </a>
            <a
              v-else-if="entry.internal_name == 'website'"
              class="body-p"
              :href="entry.val"
              target="_blank"
            >
              {{ entry.val }}
            </a>
            <p v-else-if="entry.internal_name == 'country'" class="body-p">
              {{ country }}
            </p>
            <p v-else class="body-p">
              {{ entry.val }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <contact-edit
      :isOpen="isOpen"
      :contact="contact"
      @cancel="setIsOpen(false)"
      @save="updateContact"
    ></contact-edit>

    <change-company
      :isOpen="companyChangeOpen"
      @cancel="showCompanyChange(false)"
      @save="changeCompany"
    ></change-company>
  </div>
</template>

<script>
import ContactsApi from "@/api/Contacts";
import LeadApi from "@/api/Api";
import ContactEdit from "@/components/pages/contacts/ContactEdit.vue";
import ChangeCompany from "@/components/pages/contacts/ChangeCompany.vue";
import UserImage from "@/components/helpers/Specific/UserImage";
import Ui from "@/classes/Ui";
import Countries from "@/config/data/countries";
import Languages from "@/config/data/languages";
import Loading from "@/components/helpers/Specific/Loading.vue";
import EmptyState from "@/components/helpers/DataDisplay/EmptyStateContacts";
import moment from "moment";

export default {
  components: { EmptyState, ContactEdit, ChangeCompany, UserImage, Loading },
  props: {
    contactId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      contact: {},
      isOpen: false,
      companyChangeOpen: false,
      company: {},
      leads: [],
      key: 0,
      projects: [],
      hiddenFields: [
        "company_role",
        "company",
        "firstname",
        "lastname",
        "email",
        "crawled_infos",
        "paragraph",
        "title",
        "drawing",
        "street_number",
        "company_name",
        "company_function",
        "company_streetaddress",
        "branch",
        "company_department",
        "crawled_infos",
        "academic_title",
        "free_drawing",
        "uuid",
      ],
      tags: {},
      standardCompanyFields: [
        { internal_name: "name", display_name: "Company", val: "" },
        { internal_name: "website", display_name: "Company Website", val: "" },
        { internal_name: "street", display_name: "Street", val: "" },
        {
          internal_name: "street_number",
          display_name: "Street Number",
          val: "",
        },
        { internal_name: "city", display_name: "City", val: "" },
        { internal_name: "zip", display_name: "Post Code", val: "" },
        { internal_name: "country", display_name: "Country", val: "" },
      ],
      standardPersonalFields: [
        {
          internal_name: "business_card_job_title",
          display_name: "Job Title",
          val: "",
        },
        {
          internal_name: "company_department",
          display_name: "Department",
          val: "",
        },
        {
          internal_name: "phone_mobile",
          display_name: "Mobile Phone",
          val: "",
        },
        {
          internal_name: "phone_company",
          display_name: "Company Phone",
          val: "",
        },
        {
          internal_name: "phone_private",
          display_name: "Private Phone",
          val: "",
        },
        { internal_name: "gender", display_name: "Gender", val: "" },
        { internal_name: "language", display_name: "Language", val: "" },
      ],
      leadSheets: [],
      leadsheetTabs: {},
      loading: true,
    };
  },
  computed: {
    planIsTalent() {
      return this.$store.getters.getCurrentPlanIsTalent;
    },
    userLanguage() {
      return this.$store.getters.getCurrentLanguage;
    },
    // eslint-disable-next-line vue/return-in-computed-property
    country() {
      if (this.company.address && this.company.address.country) {
        let countryCode = this.company.address.country.toUpperCase();
        let code = "";
        let language = this.userLanguage;
        Countries[language].forEach((country) => {
          if (countryCode == country.value) {
            code = country.name;
          }
        });
        return code;
      }
    },
    language() {
      let countryCode = this.contact.language;
      let code = "";

      if (!this.userLanguage) {
        this.userLanguage = "en";
      }
      if (Languages[this.userLanguage] != undefined) {
        Languages[this.userLanguage].forEach((language) => {
          if (countryCode == language.value) {
            code = language.name;
          }
        });
      }
      if (code != "") {
        return code;
      } else return "-";
    },
    emptyState() {
      return this.$store.getters.getEmptyState;
    },
  },
  async beforeMount() {
    // Override the default icons used for column sorting
    this.$q.icon.collapsible.icon = "keyboard_arrow_up";

    Ui.setSecondaryHeaderTabs({});
    Ui.setHeaderButtons({});

    Ui.setBreadcrumbs([
      {
        label: "ui.contact.breadcrumb_back",
        link: { name: "contacts" },
      },
    ]);
  },
  async mounted() {
    try {
      // Load basic contact and company information, including assigned projects
      await this.getContact(this.contactId);

      if (!this.contact) return false;

      Ui.setTitle({ label: this.contact.name });

      // Load all leads for this contact
      let leadIds = [];
      this.contact.projects.forEach((project) => {
        leadIds.push(project.lead_id);
      });
      await this.getLeads(leadIds);

      // Load the full project details for each lead
      let projectIds = [];
      this.contact.projects.forEach((project) => {
        projectIds.push(project.id);
      });

      await this.getProjects(projectIds);

      // Sort project list by when the lead was created, in reverse order
      this.contact.projects = this.contact.projects.sort((a, b) => {
        let aLead = this.getLeadById(a.lead_id);
        let bLead = this.getLeadById(b.lead_id);
        if (aLead === null || bLead === null) {
          return 0;
        }
        return moment(aLead.created_at).isBefore(bLead.created_at) ? 1 : -1;
      });

      // Get all tags for each lead
      this.tags = {};
      this.leads.forEach((lead) => {
        this.tags[lead.project_id] = lead.tags_list || [];
      });

      // Process all of the lead sheets for the projects
      this.getLeadSheets();
    } finally {
      // Switch from the loader to the main page
      this.loading = false;
    }
  },
  methods: {
    async getContact(id) {
      // Get the basic details for this contact
      let contactsApi = new ContactsApi();
      this.contact = await contactsApi.getContact(id);

      if (!this.contact) {
        this.$store.commit("SET_NOTIFICATION", {
          title: this.$t("errors.contact.not_found"),
          type: "error",
        });
        this.$router.push("/contacts");

        return;
      }

      this.company = this.contact.company;

      this.updateFields();
    },
    updateFields() {
      // Build array of standard fields with values for Contact
      Object.keys(this.contact).forEach((entry) => {
        this.standardPersonalFields.forEach((field) => {
          if (field.internal_name == entry) {
            field.val = this.contact[entry];
          }
        });
      });

      // Build array of standard fields with values for Company
      Object.keys(this.company).forEach((entry) => {
        this.standardCompanyFields.forEach((field) => {
          if (field.internal_name == entry) {
            field.val = this.company[entry];
          }
        });
      });

      // Add to our array of standard fields the matching values for Company Address
      if (
        typeof this.company.address == "object" &&
        this.company.address !== null
      ) {
        Object.keys(this.company.address).forEach((entry) => {
          this.standardCompanyFields.forEach((field) => {
            if (field.internal_name == entry) {
              field.val = this.company.address[entry];
            }
          });
        });
      }

      // Add to our array of standard personal fields the matching values from company
      Object.keys(this.company).forEach((entry) => {
        this.standardPersonalFields.forEach((field) => {
          if (field.internal_name == entry) {
            field.val = this.company[entry];
          }
        });
      });
    },
    async getLeads(leadIds) {
      let leadApi = new LeadApi();
      this.leads = await leadApi.getMultiple("leads", "id", leadIds);
    },
    async updateContact(contact) {
      this.isOpen = false;

      if (JSON.stringify(contact) == JSON.stringify(this.contact)) {
        return;
      }

      let api = new ContactsApi();
      let res = await api.updateContact(this.contact.id, contact).then((rs) => {
        this.contact = rs;
        this.company = rs.company;
        Ui.setTitle({ label: this.contact.name });

        this.updateFields();
      });
    },
    async changeCompany(companyId) {
      this.companyChangeOpen = false;
      let api = new ContactsApi();
      let res = await api
        .updateContact(this.contact.id, { company_id: companyId })
        .then((rs) => {
          this.contact = rs;
          this.company = rs.company;
          Ui.setTitle({ label: this.contact.name });

          this.updateFields();
        });
    },
    async getProjects(projectIds) {
      // Get projects assigned to this contact (as a lead)
      let leadApi = new LeadApi();
      this.projects = await leadApi.getMultiple("projects", "id", projectIds);
    },
    getLeadById(leadId) {
      let found = this.leads.find((lead) => {
        return lead.id == leadId;
      });
      if (found == undefined) {
        found = null;
      }
      return found;
    },
    getLeadSheets() {
      // Get all of the lead sheets assigned to the projects
      this.leadSheets = [];
      if (this.projects.length > 0) {
        this.projects.forEach((project) => {
          this.leadSheets[project.id] = project.lead_mask;
          this.leadSheets[project.id].project_id = project.id;
          this.sortLeadSheets(this.leadSheets[project.id]);
        });
      }
    },
    sortLeadSheets(unstrucutedSheets) {
      let projectId = unstrucutedSheets.project_id;
      let leadsheet = {};
      leadsheet = unstrucutedSheets;
      let arrayAttributes = [];
      let tabs = [];

      // create an array from the leadsheet object so we can mainpulate it better
      Object.keys(leadsheet.structure).forEach(function (attribute) {
        arrayAttributes.push(leadsheet.structure[attribute]);
      });

      // leadsheets tab names come in different types for the form editor, here we are splitting these into their tabs as we are not concerned about tab position
      arrayAttributes.forEach((attribute) => {
        if (attribute.parent_element) {
          let tabName = attribute.parent_element.split("_")[0];
          tabName = tabName.charAt(0).toUpperCase() + tabName.substr(1);
          tabs.push(tabName);
        }
      });

      // de-duplicate the different tab names and make an array of them
      let dedupedTabs = [...new Set(tabs)];

      this.sortTabs(
        projectId,
        _.cloneDeep(arrayAttributes),
        _.cloneDeep(dedupedTabs)
      );
    },
    sortTabs(projectId, arrayAttributes, tabs) {
      let newtabs = {};
      tabs.forEach(function (tab) {
        newtabs[tab] = [];
      });

      // here we want to push relevant attr's to the tabs we have created
      arrayAttributes.forEach((attribute) => {
        if (attribute.parent_element) {
          let tabName = attribute.parent_element.split("_")[0];
          tabName = tabName.charAt(0).toUpperCase() + tabName.substr(1);
          if (Object.keys(newtabs).indexOf(tabName) > -1) {
            newtabs[tabName].push(attribute);
          }
        }
      });

      // delete known tabs that we do not want to show
      delete newtabs.Root;
      if (newtabs.kits || newtabs.statistics) {
        delete newtabs.kits;
        delete newtabs.statistics;
      }

      let listToDisplay = {};
      tabs.forEach((tab) => {
        listToDisplay[tab] = [];
      });

      let personalFields = [];
      this.standardPersonalFields.forEach((field) => {
        personalFields.push(field.internal_name);
      });

      let companyFields = [];
      this.standardCompanyFields.forEach((field) => {
        companyFields.push(field.internal_name);
      });

      let filteredTabs = [];
      let companyFieldsArray = [];

      Object.keys(this.company).forEach((field) => {
        if (!companyFields.includes(field)) {
          if (field) {
            companyFieldsArray.push(field);
          }
        }
      });

      let hiddenFields = this.hiddenFields;

      // remove all entries from project section that are a standard leadsheet attr
      Object.keys(newtabs).forEach(function (newtab) {
        newtabs[newtab].forEach(function (item) {
          if (item.label) {
            if (
              !personalFields.includes(item.name) &&
              !companyFields.includes(item.name) &&
              !hiddenFields.includes(item.name) &&
              !hiddenFields.includes(item.name.split("-")[0])
            ) {
              filteredTabs.push(item);
            }
          }
        });
      });

      let projectLead = undefined;
      if (Array.isArray(this.leads)) {
        projectLead = this.leads.find((lead) => {
          return lead.project_id == projectId;
        });
      }

      if (projectLead == undefined) {
        return;
      }

      // Combine lead sheet with lead attributes
      filteredTabs.forEach((attribute) => {
        if (Object.keys(projectLead).indexOf(attribute.name) >= 0) {
          // Grab the value for this lead sheet attribute from the lead
          let rawValue = projectLead[attribute.name];
          let value = null;

          // If the attribute has a list of options, then convert the lead
          // value (or values) to the corresponding option label
          if (Array.isArray(attribute.allowed_values)) {
            // At this point, the lead value can either be a single scalar value,
            // an array of values, or an object of value/subValue pairs.
            // Here, we convert each type to a simple array, so that
            // we can process them the same way.
            let valueType = "scalar";
            let aValues = [];
            if (Array.isArray(rawValue)) {
              aValues = rawValue;
              valueType = "array";
            } else if (typeof rawValue == "object" && rawValue !== null) {
              aValues = Object.keys(rawValue);
              valueType = "object";
            } else {
              aValues = [rawValue];
            }

            // Match each raw value to the label from the lead sheet
            let processedValues = aValues.map((val) => {
              let found = attribute.allowed_values.find((option) => {
                return option.value == val;
              });
              let foundVal = val;
              if (found != undefined) {
                foundVal = found.name;
              }
              if (valueType === "object") {
                foundVal = {
                  label: foundVal,
                  value: rawValue[val],
                };
              }
              return foundVal;
            });

            if (attribute.element_type == "quantity_selection") {
              // Special case for Quanity Selectio types: remove any with a value of 0
              processedValues = processedValues.filter((value) => {
                return value.value > 0;
              });
            }

            // convert back to a scalar if necessary
            if (valueType == "scalar") {
              value = processedValues[0];
            } else {
              value = processedValues;
            }
          } else {
            value = rawValue;
          }

          let tabName = attribute.parent_element.split("_")[0];
          tabName = tabName.charAt(0).toUpperCase() + tabName.substr(1);

          let element = {
            name: attribute.name,
            label: attribute.label,
            value: value,
            content: attribute.content,
            type: attribute.element_type,
          };

          listToDisplay[tabName].push(element);
        }
      });

      delete listToDisplay.Root;

      this.leadsheetTabs[projectId] = listToDisplay;
    },
    setIsOpen(bool) {
      this.isOpen = bool;
    },
    showCompanyChange(bool) {
      this.companyChangeOpen = bool;
    },
    goToLead(projectId, leadId) {
      this.$router.push({
        path: `/project/${projectId}/leads/${leadId}/edit/`,
      });
    },
    goToCompany(id) {
      this.$router.push({
        path: `/company/${id}`,
      });
    },
    getProjectSubLabel(project) {
      let placeLabel = project.location ? project.location : "";

      let startDate = this.formatDateTimeNatural(project.start_date, {
        includeTime: false,
      });
      let endDate = this.formatDateTimeNatural(project.end_date, {
        includeTime: false,
      });

      let dateLabel = "";
      if (startDate == endDate) {
        dateLabel = startDate;
      } else {
        if (startDate != "") {
          dateLabel = startDate;
        }
        if (startDate != "" && endDate != "") {
          dateLabel += " > ";
        }
        if (endDate != "") {
          dateLabel += endDate;
        }
      }

      if (placeLabel != "" && dateLabel != "") {
        return placeLabel + " - " + dateLabel;
      }
      if (placeLabel != "") {
        return placeLabel;
      }
      return dateLabel;
    },
    convertArrayForDisplay(array, operator = "and") {
      if (array.length == 0) {
        return "";
      }
      if (array.length == 1) {
        return array[0];
      }

      let last = array.pop();
      return (
        array.join(", ") +
        " " +
        this.$t("ui.xfilters.conditions.operators." + operator) +
        " " +
        last
      );
    },
  },
};
</script>

<style lang="sass">
.contact-page
  .link
    cursor: pointer
  .block
    display: block
    padding: 0px

  .blue-text
    margin: 0px
    margin-top: 7px
    cursor: pointer

  .contact-page-loader
    z-index: 200
    position: absolute
    top: 0
    left: 0
    margin: 0 auto
    width: 100%
    height: 100%
  .padding-left
    padding-left: 30px
  .q-item-label
    padding: 20px 20px 30px 0px
  .contact-info
    text-align: center
    padding: 0px 15px 20px 15px
    .source
      text-align: center
      display: flex
      align-items: flex-end
      justify-content: center
      margin-top: 20px
      .source-icon
      .desc
        padding-left: 15px
        p.body-p
          color: $ink-light
          margin-bottom: 0px !important
    h4
      margin: 20px 0px 0px 0px
    .email
      color: $ink-lighter
      margin-bottom: 20px !important
    .caption
      margin-bottom: 0px
      color: $ink-lighter !important

  .caption
    font-weight: $font-medium !important
    color: $ink-lighter !important
    margin-bottom: 6px
  .project-wrapper
    margin-bottom: 20px

  // Project box title section
  .project-container
    background: $paper-light
    border-radius: 5px
    line-height: 20px
    .project-title
      flex-grow: 1
      .project-title-label
        font-size: 20px
        line-height: 28px
        color: #212736
        padding: 0px
        margin-bottom: 8px
      .project-title-sublabel
        font-size: 12px
        line-height: 18px
        color: #9198AB
        margin: 5px 0 14px 0
        font-weight: 500
      .project-title-classification
        width: auto
    .q-collapsible-sub-item
      margin: 0
      padding: 0
    .leadsheet-details
      padding: 20px 30px 20px 30px
      border: 2px solid $paper-light
      border-radius: 0px 0px 5px 5px
      background: white
      line-height: 20px
      .leadsheet-content
        .leadsheet-tab-wrapper
          .leadsheet-tab
            h5
              margin: 0px 0px 8px 0px
              color: $ink
            h6
              margin: 8px 0 6px 0
              color: $ink
            .heading
              font-size: 18px!important
              color: $ink
          &+.leadsheet-tab-wrapper
            margin-top: 10px

  .info-block
    padding: 8px 0px
    p.caption
      font-weight: $font-medium !important
      color: $ink-lighter !important
      margin-bottom: 6px
    p.body-p
      color: $ink
      margin: 0px
    a.body-p
      font-size: 14px
      font-weight: $font-medium
      color: $ink
      margin: 0px
    .tag-list--item
      padding: 7px 12px
      background: #dfe2e8
      border-radius: 3px
      -webkit-user-select: none
      -moz-user-select: none
      -ms-user-select: none
      user-select: none
      margin-right: 5px
      color: #212736
      height: 30px
      display: inline-block
      font-size: 12px
      margin: 5px 10px 5px 0
    .info-aggregate
      display: block
      .info-aggregate-label
        display: inline-block
        &:after
          content: ':'
      .info-aggregate-value
        display: inline-block
        margin-left: 0.5em

  .value-classification
    display: inline
    border-radius: 50%
    width: 18px
    height: 18px
    position: absolute
    margin-top: 0px !important
    &::before
      content: ''
      position: absolute
      border-radius: 50%
      width: 6px
      height: 6px
      top: 6px
      left: 6px
    &.cold
      background-color: $lead-cold
      &::before
        background-color: $lead-cold-dark
    &.warm
      background-color: $lead-warm
      &::before
        background-color: $yellow-dark
    &.hot
      background-color: $lead-hot
      &::before
        background-color: $red-dark
    &.unclassified
      background-color: $lead-unclassified
      &::before
        background-color: $ink

  .contact-details
    padding: 20px 30px 20px 30px
    background: $paper-lighter
    border-radius: 5px
    line-height: 20px
    margin-bottom: 20px
    h5, h4
      margin: 0px 0px 8px 0px
      color: $ink
    .contact-details-header
      a
        margin: 0

  .lead-panel
    .q-collapsible
      .q-collapsible-inner
        .q-item
          padding: 20px 30px
          .q-item-main
            .q-item-label
              font-size: 20px
              line-height: 28px
              color: $ink-dark
              padding: 0px
            .q-item-sublabel
              font-size: 12px
              line-height: 18px
              color: $ink-lighter
              margin-top: 5px
              font-weight: $font-medium
</style>



// WEBPACK FOOTER //
// Contact.vue?ac44c95e